import { FormField, Select } from '../ui/Form';
import { Filter, Calendar, RefreshCw, BarChart3, FileText, User } from 'lucide-react';
import type { EnhancedJournalFilters } from '../../hooks/useEnhancedJournalFilters';

interface EnhancedJournalFiltersProps {
  filters: EnhancedJournalFilters;
  onFiltersChange: (filters: Partial<EnhancedJournalFilters>) => void;
  onReset: () => void;
  filterStats: {
    totalEntries: number;
    filteredCount: number;
    filteredPercentage: number;
    totalDebit: number;
    totalCredit: number;
    isBalanced: boolean;
  };
  journalTypeOptions: Array<{ value: string; label: string }>;
  statusOptions: Array<{ value: string; label: string }>;
  sourceOptions: Array<{ value: string; label: string }>;
  isFiltersActive: boolean;
}

/**
 * Filtres métier pour les écritures comptables
 * Note: La recherche textuelle est gérée par DataTable (enableGlobalFilter)
 * Ce composant se concentre sur les filtres métier spécifiques
 */
export function EnhancedJournalFilters({ 
  filters, 
  onFiltersChange, 
  onReset, 
  filterStats,
  journalTypeOptions,
  statusOptions,
  sourceOptions,
  isFiltersActive
}: EnhancedJournalFiltersProps) {
  const handleFilterChange = (field: keyof EnhancedJournalFilters, value: string) => {
    onFiltersChange({
      [field]: value
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      {/* Header avec statistiques */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <div>
            <h3 className="font-medium text-foreground">Filtres métier</h3>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <BarChart3 className="h-3 w-3" />
                {filterStats.filteredCount} / {filterStats.totalEntries} écritures
                {filterStats.filteredPercentage < 100 && (
                  <span className="text-primary">({filterStats.filteredPercentage}%)</span>
                )}
              </span>
              <span className={`flex items-center gap-1 ${filterStats.isBalanced ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {filterStats.isBalanced ? '✓' : '⚠'} 
                {filterStats.isBalanced ? 'Équilibré' : 'Déséquilibré'}
              </span>
            </div>
          </div>
        </div>
        {isFiltersActive && (
          <button
            onClick={onReset}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Réinitialiser
          </button>
        )}
      </div>

      {/* Grille de filtres métier (sans recherche - gérée par DataTable) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 p-4">
        {/* Type de journal */}
        <FormField label="Type de journal">
          <div className="relative">
            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
            <Select
              value={filters.journalType}
              onChange={(e) => handleFilterChange('journalType', e.target.value)}
              options={journalTypeOptions}
              className="pl-10"
            />
          </div>
        </FormField>

        {/* Statut */}
        <FormField label="Statut">
          <Select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            options={statusOptions}
          />
        </FormField>

        {/* Source */}
        <FormField label="Source">
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
            <Select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              options={sourceOptions}
              className="pl-10"
            />
          </div>
        </FormField>

        {/* Date début */}
        <FormField label="Date début">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-ring text-sm bg-background text-foreground"
            />
            {filters.startDate && (
              <button
                onClick={() => handleFilterChange('startDate', '')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            )}
          </div>
        </FormField>

        {/* Date fin */}
        <FormField label="Date fin">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-ring text-sm bg-background text-foreground"
            />
            {filters.endDate && (
              <button
                onClick={() => handleFilterChange('endDate', '')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            )}
          </div>
        </FormField>
      </div>

      {/* Résumé des filtres actifs */}
      {isFiltersActive && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap gap-2">
            {filters.journalType && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs rounded-full">
                <FileText className="h-3 w-3" />
                {journalTypeOptions.find(opt => opt.value === filters.journalType)?.label}
                <button 
                  onClick={() => handleFilterChange('journalType', '')}
                  className="hover:bg-green-200 dark:hover:bg-green-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 text-xs rounded-full">
                {statusOptions.find(opt => opt.value === filters.status)?.label}
                <button 
                  onClick={() => handleFilterChange('status', '')}
                  className="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
            {filters.source && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 text-xs rounded-full">
                <User className="h-3 w-3" />
                {sourceOptions.find(opt => opt.value === filters.source)?.label}
                <button 
                  onClick={() => handleFilterChange('source', '')}
                  className="hover:bg-orange-200 dark:hover:bg-orange-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
            {filters.startDate && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200 text-xs rounded-full">
                <Calendar className="h-3 w-3" />
                Du {new Date(filters.startDate).toLocaleDateString('fr-FR')}
                <button 
                  onClick={() => handleFilterChange('startDate', '')}
                  className="hover:bg-cyan-200 dark:hover:bg-cyan-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
            {filters.endDate && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200 text-xs rounded-full">
                <Calendar className="h-3 w-3" />
                Au {new Date(filters.endDate).toLocaleDateString('fr-FR')}
                <button 
                  onClick={() => handleFilterChange('endDate', '')}
                  className="hover:bg-cyan-200 dark:hover:bg-cyan-800 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
